import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { silentRefresh } from "@/store/slices/authSlice";
import { useAuth } from "@/hooks/useAuth";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ScrollToTop from "./components/common/ScrollToTop";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import HomePage from "./components/home/HomePage";
import LoginForm from "./components/auth/LoginForm";
import RegisterForm from "./components/auth/RegisterForm";
import FullscreenMap from "./components/map/FullscreenMap";
import RouteDetails from "./components/routes/RouteDetails";
import ProfilePage from "./components/profile/ProfilePage";
import LeaderboardPage from "@/components/leaderboard/LeaderboardPage";
import EventsPage from "@/components/events/EventsPage";
import EventDetailPage from "@/components/events/EventDetailPage";
import "react-toastify/dist/ReactToastify.css";
import "leaflet/dist/leaflet.css";

// --- Admin Layout Wrapper ---
import AdminLayout from "@/components/admin/layout/AdminLayout";
import AdminDashboard from "@/components/admin/dashboard/AdminDashboard";
import UsersList from "@/components/admin/users/UsersList";
import RoutesList from "@/components/admin/routes/RoutesList";
import EventsList from "@/components/admin/events/EventsList";
import AdminAuditDashboard from "@/components/admin/audit/AdminAuditDashboard";
import AdminSettings from "@/components/admin/settings/AdminSettings";
import OrganizersPage from "@/components/admin/organizers/OrganizersPage";
import SponsorApplicationsList from "@/components/admin/sponsors/SponsorApplicationsList";
import ModeratorsPage from "@/components/admin/moderators/ModeratorsPage";
import TermsAndConditions from "@/components/legal/TermsAndConditions";
import PrivacyPolicy from "@/components/legal/PrivacyPolicy";

// --- Organizer Portal ---
import OrganizerLogin from "@/components/organizer/auth/OrganizerLogin";
import OrganizerApply from "@/components/organizer/auth/OrganizerApply";
import OrganizerLayout from "@/components/organizer/layout/OrganizerLayout";
import OrganizerDashboard from "@/components/organizer/dashboard/OrganizerDashboard";
import OrganizerEventsList from "@/components/organizer/events/OrganizerEventsList";
import EventFormModal from "@/components/organizer/events/EventFormModal";
import EventViewModal from "@/components/organizer/events/EventViewModal";
import EventRegistrationsModal from "@/components/organizer/events/EventRegistrationsModal";
import OrganizerSettings from "@/components/organizer/settings/OrganizerSettings";

// --- Sponsor Portal ---
import SponsorLogin from "@/components/sponsor/auth/SponsorLogin";
import SponsorApply from "@/components/sponsor/auth/SponsorApply";
import SponsorLayout from "@/components/sponsor/layout/SponsorLayout";
import SponsorDashboard from "@/components/sponsor/dashboard/SponsorDashboard";
import SponsorEventsPage from "@/components/sponsor/events/SponsorEventsPage";
import SponsorEventDetails from "@/components/sponsor/events/SponsorEventDetails";
import SponsorInterests from "@/components/sponsor/interests/SponsorInterests";
import SponsorSettings from "@/components/sponsor/settings/SponsorSettings";

import UIPlayground from "@/components/common/UIPlayground";
import { ThemeProvider } from "@/context/ThemeContext";

function AdminWrapper() {
  const { user, isAdminOrModerator, isAuthenticated, isLoading } = useAuth();
  const [collapsed, setCollapsed] = React.useState(true); // Start collapsed

  // If fetching profile on refresh, show loading instead of redirecting
  if (isLoading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
         <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading admin portal...</p>
         </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdminOrModerator) {
    return <Navigate to="/login" replace />;
  }

  return <AdminLayout collapsed={collapsed} setCollapsed={setCollapsed} />;
}

function App() {
  const dispatch = useDispatch();

  // Attempt silent refresh on page load to restore session via cookie
  useEffect(() => {
    dispatch(silentRefresh());
  }, [dispatch]);

  return (
    <ThemeProvider>
      <ErrorBoundary>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <Routes>
              {/* Route without Header/Footer for fullscreen map */}
              <Route path="/map" element={<FullscreenMap />} />

              {/* Admin Panel */}
              <Route path="/admin" element={<AdminWrapper />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<UsersList />} />
                <Route path="routes" element={<RoutesList />} />
                <Route path="events" element={<EventsList />} />
                <Route path="organizers" element={<OrganizersPage />} />
                <Route path="sponsors" element={<SponsorApplicationsList />} />
                <Route path="moderators" element={<ModeratorsPage />} />
                <Route path="audit" element={<AdminAuditDashboard />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>

              {/* Organizer Portal - Public Pages */}
              <Route path="/organizer/login" element={<OrganizerLogin />} />
              <Route path="/organizer/apply" element={<OrganizerApply />} />
              
              {/* Organizer Portal - Protected Dashboard */}
              <Route path="/organizer" element={<OrganizerLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<OrganizerDashboard />} />
                <Route path="events" element={<OrganizerEventsList />} />
                <Route path="events/create" element={<EventFormModal />} />
                <Route path="events/:id" element={<EventViewModal />} />
                <Route path="events/:id/edit" element={<EventFormModal />} />
                <Route path="events/:id/registrations" element={<EventRegistrationsModal />} />
                <Route path="settings" element={<OrganizerSettings />} />
              </Route>

              {/* Sponsor Portal - Public Pages */}
              <Route path="/sponsor/login" element={<SponsorLogin />} />
              <Route path="/sponsor/apply" element={<SponsorApply />} />
              
              {/* Sponsor Portal - Protected Dashboard */}
              <Route path="/sponsor" element={<SponsorLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<SponsorDashboard />} />
                <Route path="events" element={<SponsorEventsPage />} />
                <Route path="events/:id" element={<SponsorEventDetails />} />
                <Route path="interests" element={<SponsorInterests />} />
                <Route path="settings" element={<SponsorSettings />} />
              </Route>

              {/* Regular routes with Header/Footer */}
              <Route
                path="/*"
                element={
                  <>
                    <Header />
                    <main className="pt-16">
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        {/* new */}
                        <Route
                          path="/routes/:id"
                          element={<RouteDetails />}
                        />{" "}
                        {/* new */}
                        <Route path="/login" element={<LoginForm />} />
                        <Route path="/register" element={<RegisterForm />} />
                        <Route
                          path="/leaderboard"
                          element={<LeaderboardPage />}
                        />
                        <Route path="/events" element={<EventsPage />} />
                        <Route path="/events/:id" element={<EventDetailPage />} />
                        <Route
                          path="/ui-playground"
                          element={<UIPlayground />}
                        />{" "}
                        {/* <-- NEW */}
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <div className="min-h-screen flex items-center justify-center">
                                <h1 className="text-3xl font-bold text-gray-800">
                                  Dashboard - Protected Content
                                </h1>
                              </div>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route
                          path="/profile/achievements"
                          element={<ProfilePage initialTab="achievements" />}
                        />
                        <Route
                          path="/profile/settings"
                          element={<ProfilePage initialTab="settings" />}
                        />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                }
              />
            </Routes>
            <ToastContainer position="top-right" />
          </div>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
